/**
 * RF-13 · Dashboard, alertas y notificaciones (`/api/dashboard`).
 *
 * Las alertas las produce un motor de 13 reglas del backend. Cada alerta trae un
 * `enlace_frontend` con la ruta exacta del módulo que la resuelve: toda alerta se
 * atiende en un clic.
 *
 * Capa de servicios: una función por operación del API. No lanza nunca —cada
 * función devuelve un valor neutro ante el fallo—, así que el llamador debe
 * comprobar el resultado, no envolver en try/catch.
 */

import { http, primerError } from '@/services/http'

// RF-13 · Payload agregado del dashboard (una sola request) + centro de alertas.

export type Severidad = 'critica' | 'alta' | 'media' | 'informativa'

/** Forma con la que el API de dashboard devuelve este dato. */
export interface AlertaDashboard {
  clave: string
  severidad: Severidad
  titulo: string
  detalle: string | null
  id_area: number | null
  id_ejecucion: number | null
  entidad: string | null
  entidad_id: number | null
  enlace_frontend: string | null
}

/** Forma con la que el API de dashboard devuelve este dato. */
export interface DashboardPayload {
  vista: 'gestion' | 'lider' | 'consulta'
  usuario: { name: string | null; rol: string | null }
  kpis: {
    proximo_evento: { titulo: string; tipo_label: string; dias_restantes: number; fecha: string } | null
    calificacion: { valoracion: number; pct: number | null; grado: string | null; clasificacion: string; programa: string; periodo: string } | null
    avance: { calificados: number; total: number; pct: number | null }
    alertas_criticas: number
  }
  alertas: { items: AlertaDashboard[]; total: number; por_severidad: Record<Severidad, number> }
  semaforo_programas: Array<{
    id_ejecucion: number
    programa: string
    periodo: string
    estado: string
    pct_calificados: number | null
    alertas: number
    hito: { titulo: string; dias_restantes: number } | null
  }>
  proximos_eventos: Array<{
    id_evento: number
    titulo: string
    tipo_evento: string
    tipo_label: string
    programa: string | null
    fecha_inicio: string
    dias_restantes: number
  }>
  plan_salud: { total: number; por_estado: Record<string, number>; vencidas: number; pct_avance: number | null }
  mis_pendientes: {
    factores: Array<{ id_ejecucion: number; programa: string; periodo: string; factor: string; indicadores_pendientes: number; bloques_sin_redactar: number }>
    bloques_sin_redactar: number
    acciones_por_vencer: number
  } | null
  id_ejecucion_foco: number | null
  generado_en: string
}

interface Resultado<T> {
  ok: boolean
  status: number
  data?: T
  error?: string
}

/**
 * Consulta dashboard · `GET /api/dashboard`.
 *
 * @returns Resultado<DashboardPayload>
 */
export async function getDashboardService(idEjecucion?: number | null): Promise<Resultado<DashboardPayload>> {
  const sufijo = idEjecucion ? `?id_ejecucion=${idEjecucion}` : ''
  const r = await http.get<DashboardPayload>(`/api/dashboard${sufijo}`)
  return r.ok
    ? { ok: true, status: r.status, data: r.data }
    : { ok: false, status: r.status, error: primerError(r, 'No se pudo cargar el dashboard') }
}

/**
 * Consulta dashboard · `GET /api/dashboard/alertas`.
 *
 * @returns Resultado<AlertaDashboard[]>
 */
export async function getAlertasService(): Promise<Resultado<AlertaDashboard[]>> {
  const r = await http.get<AlertaDashboard[]>('/api/dashboard/alertas')
  return r.ok
    ? { ok: true, status: r.status, data: r.data }
    : { ok: false, status: r.status, error: primerError(r, 'No se pudieron cargar las alertas') }
}

/**
 * Operación sobre dashboard · `POST /api/dashboard/alertas/atender`.
 *
 * @returns Resultado<unknown>
 */
export async function atenderAlertaService(a: AlertaDashboard): Promise<Resultado<unknown>> {
  const r = await http.post('/api/dashboard/alertas/atender', {
    clave: a.clave,
    entidad: a.entidad,
    entidad_id: a.entidad_id,
    id_ejecucion: a.id_ejecucion
  })
  return r.ok
    ? { ok: true, status: r.status, data: r.data }
    : { ok: false, status: r.status, error: primerError(r, 'No se pudo marcar la alerta como atendida') }
}
